import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { ChevronDown, ChevronUp } from 'lucide-react';

const FAQS = [
  {
    category: 'General',
    questions: [
      {
        q: 'What is ResidualVault?',
        a: 'ResidualVault is a crypto staking comparison platform that tracks live APY rates across 156+ protocols. We help you find the best staking rewards for your crypto without visiting dozens of different websites.'
      },
      {
        q: 'Is ResidualVault free to use?',
        a: 'Yes! The core features are completely free. We offer paid plans (Starter, Pro, Premium) for advanced features like unlimited comparisons, portfolio tracking, and priority alerts.'
      },
      {
        q: 'How often is the APY data updated?',
        a: 'Our system fetches live APY data every 15 minutes from each protocol official API or on-chain data. You can see the last updated timestamp on each protocol detail page.'
      },
      {
        q: 'How many protocols does ResidualVault track?',
        a: 'We currently track 156 staking protocols across 17 categories including native staking, liquid staking, and DeFi yield protocols. We add new protocols regularly.'
      },
    ]
  },
  {
    category: 'Staking',
    questions: [
      {
        q: 'What is crypto staking?',
        a: 'Crypto staking is the process of locking up your cryptocurrency to help validate transactions on a Proof of Stake blockchain. In return, you earn staking rewards similar to earning interest in a savings account.'
      },
      {
        q: 'What is APY in crypto staking?',
        a: 'APY stands for Annual Percentage Yield. It represents the total return you would earn on your staked crypto over one year, including compounding. A 10% APY on $1,000 staked would earn approximately $100 over a year.'
      },
      {
        q: 'What is the difference between native staking and liquid staking?',
        a: 'Native staking means locking your crypto directly with a validator. Your tokens are locked and you cannot use them elsewhere. Liquid staking gives you a token like stETH that represents your staked position, so you can use it in DeFi while still earning rewards.'
      },
      {
        q: 'What are the risks of staking crypto?',
        a: 'Main risks include slashing (penalties for validator misbehavior), smart contract risk for liquid staking, price risk (crypto value can fall while staked), and liquidity risk (tokens may be locked). Always research thoroughly before staking.'
      },
      {
        q: 'What crypto has the highest staking APY?',
        a: 'APY rates change constantly. Check our live APY Leaderboard for current rankings. Generally, newer networks offer higher APY but with more risk, while established networks like Ethereum offer lower but more stable returns.'
      },
    ]
  },
  {
    category: 'Platform',
    questions: [
      {
        q: 'How do I compare staking protocols?',
        a: 'On the Staking page, click the compare icon on any protocol card to add it to your comparison. Add up to 5 protocols then click the floating Compare button to see a full side-by-side comparison.'
      },
      {
        q: 'How does the staking calculator work?',
        a: 'Enter an amount, select a protocol, choose a compounding frequency (daily, monthly, yearly, or none), and a time period (1-10 years). It shows your projected daily, monthly, and yearly earnings. Compare up to 5 protocols at once.'
      },
      {
        q: 'What are APY alerts?',
        a: 'APY alerts notify you by email when a protocol APY changes significantly (more than 1%). This helps you stay on top of rate changes without constantly checking the platform. Alerts are available on all paid plans.'
      },
      {
        q: 'Can I track my staking portfolio?',
        a: 'Yes! After creating a free account, you can track your staking positions in the Portfolio section of your dashboard. Enter your staked amounts and we will calculate your earnings in real time.'
      },
    ]
  },
  {
    category: 'Account & Billing',
    questions: [
      {
        q: 'How do I create an account?',
        a: 'Click Get Started Free on any page and enter your name, email, and password. No credit card required for the free plan.'
      },
      {
        q: 'What is included in the free plan?',
        a: 'The free plan includes access to all 156+ staking protocols, basic protocol comparison, the staking calculator, all blog content, and the education hub. Upgrade to a paid plan for advanced features.'
      },
      {
        q: 'Can I cancel my subscription anytime?',
        a: 'Yes, you can cancel your paid subscription at any time. You will continue to have access to paid features until the end of your billing period.'
      },
      {
        q: 'Do you offer refunds?',
        a: 'We offer a 30-day money-back guarantee on all paid plans. If you are not satisfied, contact us at support@residualvault.com for a full refund.'
      },
    ]
  },
];

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-sky-blue/20 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-steel-blue/10 transition-colors"
      >
        <span className="text-cream font-semibold pr-4">{q}</span>
        {open
          ? <ChevronUp className="w-5 h-5 text-neon-red shrink-0" />
          : <ChevronDown className="w-5 h-5 text-sky-blue shrink-0" />
        }
      </button>
      {open && (
        <div className="px-5 pb-5 text-sky-blue leading-relaxed border-t border-sky-blue/10 pt-4">
          {a}
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  return (
    <div className="min-h-screen bg-navy-dark py-16">
      <SEO
        title="Frequently Asked Questions — ResidualVault"
        description="Get answers to common questions about crypto staking, APY rates, ResidualVault features, and how to maximize your staking rewards."
        canonical="https://www.residualvault.com/faq"
        keywords="crypto staking FAQ, staking questions, what is APY, how to stake crypto, residualvault help"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4">
            <span className="text-cream">Frequently Asked </span>
            <span className="gradient-text-neon">Questions</span>
          </h1>
          <p className="text-xl text-sky-blue max-w-2xl mx-auto">
            Everything you need to know about crypto staking and ResidualVault.
          </p>
        </div>
        <div className="space-y-12">
          {FAQS.map((section) => (
            <div key={section.category}>
              <h2 className="text-2xl font-bold text-cream mb-6 flex items-center gap-3">
                <span className="w-8 h-0.5 bg-neon-red inline-block"/>
                {section.category}
              </h2>
              <div className="space-y-3">
                {section.questions.map((item) => (
                  <FAQItem key={item.q} q={item.q} a={item.a} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="card-premium text-center mt-16">
          <h2 className="text-2xl font-bold text-cream mb-3">Still have questions?</h2>
          <p className="text-sky-blue mb-6">Our team is here to help. Send us a message and we will get back to you within 24 hours.</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/contact" className="btn-neon inline-block">Contact Support</Link>
            <Link to="/blog" className="bg-steel-blue/20 border border-sky-blue text-sky-blue font-semibold px-6 py-3 rounded-lg hover:bg-steel-blue/30 transition-all inline-block">Read our guides</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
